import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { K } from '../kabinet/atoms';
import { IconChat } from '../ui/icons';

interface Обращение {
  state: 'new' | 'working' | 'awaiting' | 'solved';
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПОДДЕРЖКА — ЗНАЧКОМ В ШАПКЕ, СО СЧЁТЧИКОМ ОТКРЫТЫХ ОБРАЩЕНИЙ.
 *
 * Пакет 25.08, раздел 1, дословно: «раньше поддержка жила только
 * в меню; теперь видно, куда писать, не открывая меню».
 *
 * Довод сильнее, чем кажется. Человек, у которого что-то не работает,
 * ищет не раздел «Поддержка» — он ищет, кому пожаловаться, и ищет
 * глазами по верху экрана. Пункт в закрытом ящике находится третьим
 * действием, значок — первым.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * СЧЁТЧИК СЧИТАЕТ НЕ «ВСЕ», А «ЖДУЩИЕ ОТВЕТА»
 *
 * Решённые обращения не считаются никогда: цифра «12» у человека,
 * который за год написал двенадцать раз и все двенадцать закрыты,
 * означает «двенадцать бед» и врёт.
 *
 * Ноль не рисуется вовсе — ни серым, ни бледным. Значок без числа
 * и значок с нулём выглядят по-разному, а значат одно.
 * ═══════════════════════════════════════════════════════════════════════
 */
/**
 * Сколько обращений ждут ответа.
 *
 * ОДИН СЧЁТ НА ДВА МЕСТА: значок в шапке и подпись «Поддержки»
 * в меню разделов. Два счёта одного и того же однажды разойдутся —
 * и разойдутся на глазах у человека, в одном экране.
 */
export function useОткрытыеОбращения(wsId: string | undefined, enabled: boolean): number | null {
  const q = useQuery({
    // Общий ответ со страницей: её invalidateQueries после ответа,
    // закрытия или создания обращения обновляет и подпись, и значок.
    queryKey: ['support', wsId],
    queryFn: () => api<{ tickets: Обращение[] }>(`/workspaces/${wsId!}/support`),
    enabled: Boolean(wsId) && enabled,
    // Отказ молчит: значок ведёт в поддержку и без числа
    retry: false,
    staleTime: 60_000,
  });

  return q.data === undefined ? null : q.data.tickets.filter((t) => t.state !== 'solved').length;
}

/** До первого открытия меню или поддержки число ещё неизвестно. */
export function SupportButton({ открытых }: { открытых: number | null }) {
  return (
    <Link
      to="support"
      data-d="кнопка-поддержки"
      title="Поддержка — написать в сервис"
      aria-label="Поддержка"
      className={`${K.кнопкаПоддержки} no-underline`}
    >
      <IconChat />
      {открытых !== null && открытых > 0 && <span className={K.счётчикНаЗначке}>{открытых}</span>}
    </Link>
  );
}
